import {
    Connection,
    PublicKey,
    Transaction,
    SystemProgram,
    TransactionInstruction,
    sendAndConfirmRawTransaction
} from "@solana/web3.js";
import {
    createTransferInstruction,
    getAssociatedTokenAddress,
    TOKEN_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID
} from "@solana/spl-token";
import { _GLOBALS } from '../globals.js';
import { logit } from './simpleLog.js';
import { Solana } from "@quicknode/sdk";

const rpc_endpoint = _GLOBALS.SolanaRpc;
const memoProgramId = new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr");

const getProvider = (walletName = "") => {
    if (walletName === "Phantom") {
        if (!('solana') in window || !window.solana.isPhantom) {
            throw new Error("Phantom wallet not found! Please install it.");
        } else {
            return window.solana;
        }
    }

    if (walletName === "Coinbase") {
        // Get Coinbase Solana Provider
        if (!('coinbaseSolana' in window)) {
            throw new Error("Coinbase wallet not found! Please install it.");
        } else {
            return window.coinbaseSolana;
        }
    }
    
    if (walletName === "Solflare") {
        // Get Solflare Solana Provider
        if (!('solflare' in window)) {
            throw new Error("Solflare wallet not found! Please install it.");
        } else {
            return window.solflare;
        }
    }
    throw new Error(`${walletName} currently not supported. Please use Phantom, Coinbase or Solflare`);
}

export async function transferSPLToken(
        selectedWallet,
        destinationAddress,
        sourceTokenAccountAddress,
        tokenMintAddress,
        amount,
        referralProgram = ""
        ) {

    const walletName = selectedWallet.name;

    /*
     * Differentiate user selected wallets
     */
    let provider = getProvider(walletName);
    await provider.connect();

    const connection = new Connection(rpc_endpoint, {
        commitment: 'confirmed',
        confirmTransactionInitialTimeout: 60000
    });

    const walletPublicKey = provider.publicKey;
    const destinationPublicKey = new PublicKey(destinationAddress);
    const tokenMint = new PublicKey(tokenMintAddress);
    const sourceTokenAccount = new PublicKey(sourceTokenAccountAddress);

    // Get the recipient's associated token account
    const destinationTokenAccount = await getAssociatedTokenAddress(
            tokenMint,
            destinationPublicKey,
            false, // allowOwnerOffCurve
            TOKEN_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
            );

    if (_GLOBALS.DebugMode)
        logit(`Recipient Token Account ${destinationTokenAccount.toString()}`);

    // Create a transfer instruction
    const transferInstruction = createTransferInstruction(
            sourceTokenAccount, // Source token account
            destinationTokenAccount, // Recipient token account
            walletPublicKey, // Owner of the source account
            amount,
            [], // Additional signers if necessary
            TOKEN_PROGRAM_ID
            );

    // Check if the recipient's token account exists
    const recipientAccountInfo = await connection.getAccountInfo(destinationTokenAccount);
    if (!recipientAccountInfo) {
        console.log("Recipient's associated token account does not exist, it will be created automatically.");
    }
    const transaction = new Transaction().add(transferInstruction);

    // Create Memo Instructions
    const memoInstruction = new TransactionInstruction({
        keys: [],
        programId: memoProgramId,
        data: Buffer.from(referralProgram), // The memo content
    });
    transaction.add(memoInstruction); // Add the memo instruction

    transaction.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;
    transaction.feePayer = walletPublicKey;

    /* optimize */
    const endpoint = new Solana({endpointUrl: rpc_endpoint});

    const optimizedTransaction = await endpoint.prepareSmartTransaction({
        transaction,
        payerPublicKey: walletPublicKey,
        feeLevel: "extreme"
    });

    // Sign the transaction using Phantom
    let signedTransaction = null;
    try {
        console.log("Sending transaction to Phantom for signing...");
        signedTransaction = await provider.signTransaction(optimizedTransaction);
        if (_GLOBALS.DebugMode) logit("--- SIGNED TRANSACTION ---");
        if (_GLOBALS.DebugMode) logit(signedTransaction);
    } catch (err) {
        if (_GLOBALS.DebugMode) logit("--- [ signedTransaction TRY CATCH TRIGGERED ]---");
        if (_GLOBALS.DebugMode) logit(err.message);
        console.log(err);
        throw new Error(err.message);
    }

    try {
        // Send the signed transaction
        const confirmOptions = {
            commitment: "confirmed", // Wait until the transaction is fully finalized
//      commitment: "finalized", // Wait until the transaction is fully finalized
            maxRetries: 5, // Retry up to 5 times if the transaction fails to send
            //preflightCommitment: "finalized", // Use lower commitment for preflight check
            skipPreflight: true, // Perform transaction simulation before sending
        };
        if (_GLOBALS.DebugMode) logit("--- Sending ---");

        const txid = await sendAndConfirmRawTransaction(connection, signedTransaction.serialize(), confirmOptions);

        if (_GLOBALS.DebugMode) logit("--- TX ID ---");
        if (_GLOBALS.DebugMode) logit(txid);
        return txid;

    } catch (err) {
        if (_GLOBALS.DebugMode) logit("--- [ sendTransaction TRY CATCH TRIGGERED ]---");
        if (_GLOBALS.DebugMode) logit(err.message);
        console.log(err);
        throw new Error(err.message);
    }
}

export const signingProcess = async(txData = null, selectedWallet = null) => {
//    userPublicKey: _PUBKEY,
//    userPaymentMintAddress: _SELECTED_PAY_TOKEN.mint,
//    amount: amount,
//    referral: _GLOBALS.ReferralProgram
    const presignOptions = {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(txData)
    };
    const presign = await fetch('https://backend.equichain.org/api/transaction/presign', presignOptions);
    const presignResponse = await presign.json();
    if(!presignResponse.success) {
        throw new Error('An error occured during presigning');
        return false;
    }
    const presignData = JSON.parse(presignResponse.data);
     const receivedTx = Transaction.from(Buffer.from(presignData.tx, 'base64'));

    const walletName = selectedWallet.name;
    let provider = getProvider(walletName);
    await provider.connect();
 
    const signedTransaction = await provider.signTransaction(receivedTx);

    // TODO: Delete for production 
    // ----------------------------
//    console.log("signedTransaction", signedTransaction);
//    const txId = "TESTMODE-SUCCESS";
    // ----------------------------
    
    

    // TODO: activate for productive usage again
    // ----------------------------------
    const connection = new Connection(rpc_endpoint, {
        commitment: 'confirmed',
        confirmTransactionInitialTimeout: 60000
    });
    const txId = await connection.sendRawTransaction(signedTransaction.serialize());
    if(txId) {
        console.log("SignedTransaction", txId);
        return txId;
    }
    // ----------------------------------
    // END TOTO ---

    
        
//    const signedTransaction = await provider.signTransaction(receivedTx);    
//    const serializeTransaction = receivedTx.serialize({ requireAllSignatures: false });
    
//    const signOptions = {
//        method: "POST",
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify({txData: serializeTransaction.toString('base64')})
//    };
//    const send = await fetch('http://localhost:3003/api/transaction/send', signOptions);
//    const sendResponse = await send.json();
//    if(!sendResponse.success) {
//        throw new Error('An error occured during sending transaction');
//        return false;
//    }
//    console.log("[send]", sendResponse);
    
//    return {success: true, txId: sendResponse.transactionId};
}
